import React, { useState, useEffect } from 'react';

// material-ui
import { Grid, Typography, Table, TableBody, TableCell, TableRow, TableContainer, TableHead, Tooltip } from '@mui/material';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';

// assets
import useLeagues from 'hooks/useLeagues';
import { useTheme } from '@mui/material/styles';

// project imports
import * as sorters from 'utils/statsSorters';
import PlayerCell from 'views/components/playerCell.component';
import getPointDifferentialPercentage from 'utils/pointDifferentialPercentage';

const LeagueStatsTable = ({ eventName }) => {
    const theme = useTheme();

    const { leagueStats } = useLeagues();

    const [sortedLeagueStats, setSortedLeagueStats] = useState();

    const [currentSort, setCurrentSort] = useState('');

    function toggleXSort(descStr, ascStr, sortFcnAsc, sortFcnDesc) {
        if (!sortedLeagueStats || sortedLeagueStats.length === 0) {
            return;
        }
        const resortedStats = sortedLeagueStats;

        if (currentSort === descStr) {
            resortedStats.sort(sortFcnAsc);
            setCurrentSort(ascStr);
        } else {
            resortedStats.sort(sortFcnDesc);
            setCurrentSort(descStr);
        }
        setSortedLeagueStats(resortedStats);
    }

    function togglePointDifferentialSort() {
        toggleXSort('PD_DESC', 'PD_ASC', sorters.sortByPointDifferentialAsc, sorters.sortByPointDifferentialDesc);
    }

    function togglePointsAllowedSort() {
        toggleXSort('PA_DESC', 'PA_ASC', sorters.sortByPointsAllowedAsc, sorters.sortByPointsAllowedDesc);
    }

    function togglePointsEarnedSort() {
        toggleXSort('PE_DESC', 'PE_ASC', sorters.sortByPointsEarnedAsc, sorters.sortByPointsEarnedDesc);
    }

    function toggleLossesSort() {
        toggleXSort('LOSSES_DESC', 'LOSSES_ASC', sorters.sortByLossesAsc, sorters.sortByLossesDesc);
    }

    function toggleWinLossPctgSort() {
        toggleXSort('WL_PCTG_DESC', 'WL_PCTG_ASC', sorters.sortByWinLossAsc, sorters.sortByWinLossDesc);
    }

    function toggleWinsSort() {
        toggleXSort('WINS_DESC', 'WINS_ASC', sorters.sortByWinsAsc, sorters.sortByWinsDesc);
    }

    function toggleDifferentialPercentageSort() {
        toggleXSort('PDP_DESC', 'PDP_ASC', sorters.sortByPointDifferentialPercentageAsc, sorters.sortByPointDifferentialPercentageDesc);
    }

    function toggleNameSort() {
        toggleXSort('NAME_DESC', 'NAME_ASC', sorters.sortByNameAsc, sorters.sortByNameDesc);
    }

    function toggleDisproportionSort() {
        toggleXSort('DISP_DESC', 'DISP_ASC', sorters.sortByDisproportionAsc, sorters.sortByDisproportionDesc);
    }

    useEffect(() => {
        if (leagueStats.length !== 0) {
            setSortedLeagueStats(leagueStats);
        }
    }, [leagueStats]);

    useEffect(() => {
        // "if" is here to ensure we only default-sort one time.
        if (currentSort === '') {
            toggleWinsSort();
        }
    }, [sortedLeagueStats]);

    function renderRow(userAttributes) {
        return (
            <TableRow key={userAttributes?.name}>
                <TableCell
                    sx={{
                        flexDirection: 'column',
                        minWidth: '200px',
                        flex: 1,
                        whiteSpace: 'nowrap'
                    }}
                >
                    <PlayerCell userAttributes={userAttributes} />
                </TableCell>
                <TableCell align="center">{userAttributes.wins}</TableCell>
                <TableCell align="center">{userAttributes.losses}</TableCell>
                <TableCell align="center">{(sorters.getWinLossPercentage(userAttributes) * 100).toFixed(1)}%</TableCell>
                <TableCell align="center">{userAttributes.points_earned}</TableCell>
                <TableCell align="center">{userAttributes.points_against}</TableCell>
                <TableCell align="center">{userAttributes.point_differential}</TableCell>
                <TableCell align="center">
                    {getPointDifferentialPercentage(userAttributes.points_earned, userAttributes.points_against)}
                </TableCell>
                <TableCell align="center">
                    {sorters.calculateDisproportionalityScore(
                        userAttributes.points_earned - userAttributes.points_against,
                        userAttributes.wins,
                        userAttributes.losses
                    )}
                </TableCell>
            </TableRow>
        );
    }

    function renderSortableTableHeaderCell(sortFunction, ascStr, descStr, text) {
        const shouldRenderArrow = currentSort.includes(ascStr) || currentSort.includes(descStr);
        return (
            <TableCell align="center">
                <Grid
                    onClick={() => sortFunction()}
                    sx={{
                        borderRadius: '5px',
                        backgroundColor: shouldRenderArrow ? theme.palette.success[200] : theme.palette.primary.light,
                        paddingLeft: 1,
                        paddingRight: 1
                    }}
                    container
                >
                    <Grid item xs={shouldRenderArrow ? 4 : 5} />
                    <Grid item xs={shouldRenderArrow ? 2 : 1} sx={{ pt: '3px' }}>
                        <Typography sx={{ float: shouldRenderArrow ? 'right' : 'none', textAlign: 'center' }}>{text}</Typography>
                    </Grid>
                    <Grid item xs={2} sx={{ height: '24px' }}>
                        <ArrowUpwardIcon
                            sx={{
                                display: currentSort === ascStr ? 'inline-block' : 'none',
                                fontSize: '18px',
                                mt: '3px',
                                float: 'left'
                            }}
                        />
                        <ArrowDownwardIcon
                            sx={{
                                display: currentSort === descStr ? 'inline-block' : 'none',
                                fontSize: '18px',
                                mt: '3px',
                                float: 'left'
                            }}
                        />
                    </Grid>
                    <Grid item xs={4} />
                </Grid>
            </TableCell>
        );
    }

    function renderSortableTableHeader(sortFunction, ascStr, descStr, text, tooltipText = '') {
        if (tooltipText === '') {
            return renderSortableTableHeaderCell(sortFunction, ascStr, descStr, text);
        }
        return <Tooltip title={tooltipText}>{renderSortableTableHeaderCell(sortFunction, ascStr, descStr, text)}</Tooltip>;
    }

    return (
        <Grid>
            <TableContainer>
                <Table size="small" aria-label={eventName} sx={{ backgroundColor: 'white', borderRadius: 2 }}>
                    <TableHead>
                        <TableRow>
                            {renderSortableTableHeader(toggleNameSort, 'NAME_ASC', 'NAME_DESC', 'Players')}
                            {renderSortableTableHeader(
                                toggleWinsSort,
                                'WINS_ASC',
                                'WINS_DESC',
                                'W',
                                'Number of games this player has won.'
                            )}
                            {renderSortableTableHeader(
                                toggleLossesSort,
                                'LOSSES_ASC',
                                'LOSSES_DESC',
                                'L',
                                'Number of games this player has lost.'
                            )}
                            {renderSortableTableHeader(toggleWinLossPctgSort, 'WL_PCTG_ASC', 'WL_PCTG_DESC', 'W%', 'Win percentage.')}
                            {renderSortableTableHeader(
                                togglePointsEarnedSort,
                                'PE_ASC',
                                'PE_DESC',
                                'PE',
                                '"Points Earned", the total number of points this player got across all games.'
                            )}
                            {renderSortableTableHeader(
                                togglePointsAllowedSort,
                                'PA_ASC',
                                'PA_DESC',
                                'PA',
                                '"Points Allowed": the total number of points this player has allowed opponents to score across all games.'
                            )}
                            {renderSortableTableHeader(
                                togglePointDifferentialSort,
                                'PD_ASC',
                                'PD_DESC',
                                'PD',
                                '"Point Differential": PE - PA, or how many more points the player has gotten versus how many they allowed.'
                            )}
                            {renderSortableTableHeader(
                                toggleDifferentialPercentageSort,
                                'PDP_ASC',
                                'PDP_DESC',
                                'PD%',
                                '"Point Differential Percentage". Just PD in a percentage format.'
                            )}
                            {renderSortableTableHeader(
                                toggleDisproportionSort,
                                'DISP_ASC',
                                'DISP_DESC',
                                'DISP',
                                'Point differential disproportion. A high value means they score a lot of points relative to how much they win.'
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>{sortedLeagueStats?.map((team) => renderRow(team))}</TableBody>
                </Table>
            </TableContainer>
        </Grid>
    );
};

export default LeagueStatsTable;
